import type { Tone } from './ui/kit.js';

/* One vocabulary for the board's word, derived by the daemon from facts
 * (Ops V7 design §1.2): a job is queued, running, ended or closed, and the
 * word says which fact currently matters. The rail below it is the honest
 * journey — queue → session → PR → CI → review → main → staging (or live). */
export const RAIL_LABELS = ['Queue', 'Session', 'PR', 'CI', 'Review', 'Main', 'Staging'];
export const PAGES_RAIL_LABELS = ['Queue', 'Session', 'PR', 'Review', 'Main', 'Live'];
export const WORKSHOP_RAIL_LABELS = ['Queue', 'Session', 'Delivered'];

export const PHASE_META: Record<string, { label: string; tone: Tone; railIndex: number; live?: boolean }> = {
  scheduled: { label: 'Scheduled', tone: 'wait', railIndex: 0 },
  queued: { label: 'Queued', tone: 'wait', railIndex: 0 },
  working: { label: 'Running', tone: 'run', railIndex: 1, live: true },
  needs_input: { label: 'Needs you', tone: 'accent', railIndex: 1 },
  done: { label: 'Done', tone: 'ok', railIndex: 1 },
  pushed: { label: 'Pushed', tone: 'ok', railIndex: 2 },
  ci_running: { label: 'CI running', tone: 'run', railIndex: 3, live: true },
  red: { label: 'CI red', tone: 'bad', railIndex: 3 },
  green_held: { label: 'Ready to review', tone: 'hold', railIndex: 4 },
  merging: { label: 'Merging', tone: 'run', railIndex: 5, live: true },
  merged: { label: 'Merged', tone: 'ok', railIndex: 5 },
  staging_verified: { label: 'Staging verified', tone: 'ok', railIndex: 6 },
  live: { label: 'Live', tone: 'ok', railIndex: 5 },
  limit: { label: 'Limit reached', tone: 'bad', railIndex: 1 },
  auth: { label: 'Authentication error', tone: 'bad', railIndex: 1 },
  api_error: { label: 'API error', tone: 'bad', railIndex: 1 },
  crashed: { label: 'Crashed', tone: 'bad', railIndex: 1 },
  stopped: { label: 'Stopped', tone: 'wait', railIndex: 1 },
  budget: { label: 'Budget reached', tone: 'hold', railIndex: 1 },
  canceled: { label: 'Canceled', tone: 'wait', railIndex: 0 },
};

export const meta = (phase: string) => PHASE_META[phase] ?? { label: phase.replace(/_/g, ' '), tone: 'wait' as Tone, railIndex: 0 };

/* The phases that want a person: an open question, a red, a review, an
   ending that offers Continue. */
export const ATTENTION = ['needs_input', 'red', 'green_held', 'limit', 'auth', 'api_error', 'crashed', 'budget', 'stopped', 'done', 'pushed'];
export const MOVING = ['working', 'ci_running', 'merging'];
export const WAITING = ['queued', 'scheduled'];

/* The words for each way an attempt can end (design §2). The daemon
   decides the kind; the app chooses the sentence. */
export const ENDING_TITLE: Record<string, string> = {
  done: 'The session said it was done',
  needs_input: 'The session is waiting for you',
  limit: 'The account reached its usage limit',
  auth: 'The account token was refused',
  api_error: 'The model API answered with an error',
  crashed: 'The process ended without finishing',
  stopped: 'You stopped it',
  budget: 'The budget was reached',
};

/* ── V8 ──────────────────────────────────────────────────────────────────
 * The lifecycle is the state the daemon DRIVES (spec §3). `phase` above
 * stays what it always was — a derived word about the session's process —
 * and the two are deliberately different things: a session can be `ended`
 * while the job is `ci_red` and very much alive. The board leads with the
 * lifecycle now, because that is the one that answers "is this finished".
 */
export const LIFECYCLE_RAIL = ['Queue', 'Session', 'PR', 'CI', 'Merge', 'Main', 'Staging'];

export const LIFECYCLE_META: Record<
  string,
  { label: string; tone: Tone; railIndex: number; live?: boolean; terminal?: boolean; sub?: string }
> = {
  queued: { label: 'Queued', tone: 'wait', railIndex: 0 },
  working: { label: 'Working', tone: 'run', railIndex: 1, live: true },
  pushed: { label: 'Pushed', tone: 'ok', railIndex: 2 },
  pr_open: { label: 'PR open', tone: 'ok', railIndex: 2 },
  ci_running: { label: 'CI running', tone: 'run', railIndex: 3, live: true },
  ci_red: { label: 'CI red', tone: 'bad', railIndex: 3, sub: 'the Finisher is on it' },
  fixing: { label: 'Fixing', tone: 'run', railIndex: 3, live: true },
  ci_green: { label: 'CI green', tone: 'ok', railIndex: 4 },
  merge_queued: { label: 'In the merge queue', tone: 'hold', railIndex: 4 },
  merged: { label: 'Merged', tone: 'ok', railIndex: 5, terminal: true },
  candidate: { label: 'Candidate', tone: 'ok', railIndex: 6, terminal: true },
  staging_verified: { label: 'Verified on staging', tone: 'ok', railIndex: 6, terminal: true },
  promotable: { label: 'Ready to promote', tone: 'accent', railIndex: 6, terminal: true },
  live: { label: 'Live', tone: 'ok', railIndex: 6, terminal: true },
  blocked_on_human: { label: 'Blocked on you', tone: 'accent', railIndex: 3, terminal: true },
  budget_exhausted: { label: 'Budget spent', tone: 'hold', railIndex: 1, terminal: true },
  cycles_exhausted: { label: 'Fix cycles spent', tone: 'bad', railIndex: 3, terminal: true },
  abandoned_by_operator: { label: 'Stopped', tone: 'wait', railIndex: 1, terminal: true },
  parked_limit: { label: 'Parked at a limit', tone: 'hold', railIndex: 1, sub: 'it resumes by itself' },
  parked_capacity: { label: 'Waiting for a slot', tone: 'wait', railIndex: 0 },
};

export const lifeMeta = (l: string) =>
  LIFECYCLE_META[l] ?? { label: l.replace(/_/g, ' '), tone: 'wait' as Tone, railIndex: 0 };

/** The lifecycles that want a person to do something. */
export const LIFECYCLE_ATTENTION = ['blocked_on_human', 'cycles_exhausted', 'budget_exhausted', 'promotable'];
/* The lifecycles the daemon owns — nothing is owed by anyone.
 *
 * The parked ones belong here rather than under "Queued": a parked job has
 * started, the daemon holds it, and it resumes by itself at a time it can
 * name. "Queued" would say it had never begun. And a crash or a limit no
 * longer sits under "Waiting on you" at all, which is the V8 inversion
 * showing up as a heading: the daemon restarts a crash and parks a limit,
 * so neither is waiting on a person any more. */
export const LIFECYCLE_MOVING = ['working', 'ci_running', 'fixing', 'merge_queued', 'parked_limit', 'parked_capacity'];

/* What the Finisher decided about a red, in the app's words. The verdict is
   an argument a reader must be able to disagree with, so it never hides
   behind a colour: the reason is always shown beside it. */
export const VERDICT_META: Record<string, { label: string; tone: Tone }> = {
  real: { label: 'Real failure', tone: 'bad' },
  flake: { label: 'Known flake', tone: 'hold' },
  infrastructure: { label: 'Infrastructure', tone: 'wait' },
};

export const ACTION_WORDS: Record<string, string> = {
  fix_turn: 'started a fix turn',
  rerun_job: 're-ran that job once',
  rerun_run: 're-ran the whole run once',
  wait: 'waited',
  blocked_on_human: 'stopped and asked you',
};

/* ── Where the work actually GOT to (V9) ─────────────────────────────────
 *
 * The rail used to take its position from the lifecycle word:
 * `budget_exhausted` is mapped to rail 1, "Session", because a budget
 * usually runs out while a session is working. The admin round ran out
 * after its pull request was open and after CI had finished, and the page
 * drew it as still in Session with PR and CI "not reached" — of a job
 * whose PR was #127 and whose CI had been red for six hours.
 *
 * A word about WHY a job stopped cannot say HOW FAR it got. So the
 * furthest point is read from the facts, and the lifecycle keeps what it
 * is good for, which is the colour and the sentence.
 */
export function railReached(job: {
  attempt?: { n?: number } | null;
  startedAt?: string | null;
  prNumber?: number | null;
  mergeCommitSha?: string | null;
  lifecycle?: string;
  facts?: { ciConclusion?: 'success' | 'failure' | 'pending' | null };
}): number {
  const life = job.lifecycle ?? '';
  if (['candidate', 'staging_verified', 'promotable', 'live'].includes(life)) return 6;
  if (job.mergeCommitSha || life === 'merged') return 5;
  const ci = job.facts?.ciConclusion ?? null;
  if (ci === 'success' || life === 'ci_green' || life === 'merge_queued') return 4;
  if (ci) return 3;
  if (job.prNumber) return 2;
  if (job.startedAt || job.attempt) return 1;
  return 0;
}
